import axios from 'axios';
import { loadTtsConfig } from '../config/ttsDefaults';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export function getAudioMimeType(format = 'mp3') {
  const mimeByFormat = {
    mp3: 'audio/mpeg',
    opus: 'audio/ogg; codecs=opus',
    aac: 'audio/aac',
    flac: 'audio/flac',
    wav: 'audio/wav',
    pcm: 'audio/wav',
  };

  return mimeByFormat[format] || 'audio/mpeg';
}

function findLastBoundary(text, minChars, pattern) {
  let match;
  let boundary = -1;
  pattern.lastIndex = 0;

  while ((match = pattern.exec(text)) !== null) {
    const end = match.index + match[0].length;
    if (end >= minChars) boundary = end;
  }

  return boundary;
}

function findWordBoundary(text, maxPos) {
  if (maxPos >= text.length) return text.length;
  if (/\s/.test(text[maxPos]) || /[.!?;:,\-–—…]/.test(text[maxPos])) return maxPos;
  for (let i = maxPos - 1; i >= 0; i--) {
    if (/\s/.test(text[i]) || /[.!?;:,\-–—…]/.test(text[i])) {
      return i + 1;
    }
  }
  return maxPos;
}

export function pullTtsChunks(buffer, options = {}) {
  const minChars = options.minChars ?? 35;
  const maxChars = options.maxChars ?? 280;
  let remaining = buffer.replace(/\s+/g, ' ').trimStart();
  const chunks = [];

  while (remaining.length > 0) {
    const searchLimit = Math.min(remaining.length, maxChars + 1);
    const windowText = remaining.slice(0, searchLimit);
    let boundary = findLastBoundary(windowText, minChars, /[.!?;:…][)"'\]]?\s+/g);

    if (remaining.length <= maxChars) {
      if (boundary !== -1 && boundary < remaining.length) {
        const chunk = remaining.slice(0, boundary).trim();
        if (chunk) chunks.push(chunk);
        remaining = remaining.slice(boundary).trimStart();
        continue;
      }

      if (options.force && remaining.trim()) {
        chunks.push(remaining.trim());
        remaining = '';
      }
      break;
    }

    if (boundary === -1) {
      boundary = findLastBoundary(windowText, minChars, /[,)]\s+/g);
    }

    if (boundary === -1) {
      boundary = findLastBoundary(windowText, minChars, /[\-–—]\s+/g);
    }

    if (boundary === -1) {
      boundary = findLastBoundary(windowText, minChars, /\s+/g);
    }

    if (boundary === -1) {
      boundary = maxChars;
    }

    boundary = findWordBoundary(remaining, boundary);

    const chunk = remaining.slice(0, boundary).trim();
    if (chunk) chunks.push(chunk);
    remaining = remaining.slice(boundary).trimStart();
  }

  return { chunks, remainder: remaining };
}

export async function makeSpeech(text, options = {}) {
  const cfg = loadTtsConfig();
  const responseFormat = cfg.response_format || 'mp3';

  const headers = {};
  if (cfg.apiKey) {
    headers['x-openai-key'] = cfg.apiKey;
  }

  const response = await axios.post(
    `${API_URL}/api/tts`,
    {
      model: cfg.model || 'tts-1',
      voice: cfg.voice || 'onyx',
      input: text,
      response_format: responseFormat,
      speed: cfg.speed ?? 1.0,
    },
    {
      headers,
      responseType: 'blob',
      signal: options.signal,
    }
  );

  const audioUrl = URL.createObjectURL(
    new Blob([response.data], { type: getAudioMimeType(responseFormat) })
  );

  return { data: { filename: audioUrl } };
}

export async function streamOpenAIResponse(message, handlers = {}) {
  const cfg = loadTtsConfig();
  const chunkOptions = {
    minChars: cfg.ttsChunkMinChars ?? 35,
    maxChars: cfg.ttsChunkMaxChars ?? 280,
  };

  const chatHeaders = {
    'Content-Type': 'application/json',
  };
  if (cfg.apiKey) {
    chatHeaders['x-openai-key'] = cfg.apiKey;
  }

  const response = await fetch(`${API_URL}/api/chat`, {
    method: 'POST',
    headers: chatHeaders,
    signal: handlers.signal,
    body: JSON.stringify({
      model: cfg.chatModel || 'gpt-4o-mini',
      messages: [
        {
          role: 'system',
          content:
            cfg.systemPrompt ||
            'Você é uma assistente virtual amigável. Responda sempre em português do Brasil (pt-BR). Seja natural, prestativa e converse de forma descontraída.',
        },
        {
          role: 'user',
          content: message,
        },
      ],
      max_tokens: cfg.chatMaxTokens ?? 600,
      temperature: cfg.temperature ?? 0.7,
      stream: true,
    }),
  });

  if (!response.ok) {
    const details = await response.text().catch(() => '');
    throw new Error(`Chat proxy falhou (${response.status}): ${details || response.statusText}`);
  }

  if (!response.body) {
    throw new Error('Este navegador nao retornou um stream para a resposta.');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let sseBuffer = '';
  let fullText = '';
  let ttsBuffer = '';

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;

    sseBuffer += decoder.decode(value, { stream: true });
    const lines = sseBuffer.split('\n');
    sseBuffer = lines.pop() || '';

    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed.startsWith('data:')) continue;

      const data = trimmed.slice(5).trim();
      if (data === '[DONE]') continue;

      let payload;
      try {
        payload = JSON.parse(data);
      } catch (error) {
        console.warn('Ignorando chunk SSE invalido:', data);
        continue;
      }

      const delta = payload.choices?.[0]?.delta?.content || '';
      if (!delta) continue;

      fullText += delta;
      ttsBuffer += delta;
      handlers.onDelta?.(fullText);

      const result = pullTtsChunks(ttsBuffer, chunkOptions);
      ttsBuffer = result.remainder;
      result.chunks.forEach((chunk) => handlers.onTtsChunk?.(chunk));
    }
  }

  const finalChunks = pullTtsChunks(ttsBuffer, { ...chunkOptions, force: true });
  finalChunks.chunks.forEach((chunk) => handlers.onTtsChunk?.(chunk));

  return fullText.trim();
}
